import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

interface NextStepCardProps {
  titleId: string
  title: string
  /** 0-based index of the current step; -1 hides the counter. */
  currentIndex: number
  /** Completion flag per onboarding step, in display order. */
  completed: boolean[]
  body?: ReactNode
  icon?: ReactNode
  /** The single primary action. Omit it when the client can only wait. */
  action?: ReactNode
  /** Extra content under the action, e.g. the "see all steps" disclosure. */
  children?: ReactNode
  tone?: 'action' | 'waiting'
  as?: 'section' | 'div'
  className?: string
}

/** "Langkah x dari 7" hero: counter, segmented progress, title, one line of context, one action. */
export function NextStepCard({
  titleId,
  title,
  currentIndex,
  completed,
  body,
  icon,
  action,
  children,
  tone = 'action',
  as: Tag = 'section',
  className,
}: NextStepCardProps) {
  const { t } = useTranslation()
  const total = completed.length

  return (
    <Tag
      className={cn('prod-next-step', `prod-next-step--${tone}`, className)}
      aria-labelledby={titleId}
    >
      <div className="prod-next-step__head">
        {icon ? <span className="prod-next-step__icon">{icon}</span> : null}
        {currentIndex >= 0 ? (
          <p className="prod-next-step__count">
            {t('home.stepCount', { current: currentIndex + 1, total })}
          </p>
        ) : null}
      </div>
      {currentIndex >= 0 ? (
        <div className="prod-segments" aria-hidden="true">
          {completed.map((done, index) => (
            <span
              key={index}
              className={cn(
                'prod-segments__item',
                done && 'prod-segments__item--done',
                !done && index === currentIndex && 'prod-segments__item--current',
              )}
            />
          ))}
        </div>
      ) : null}
      <h2 id={titleId} className="prod-next-step__title">
        {title}
      </h2>
      {body ? <div className="prod-next-step__body">{body}</div> : null}
      {action ? <div className="prod-next-step__action">{action}</div> : null}
      {children}
    </Tag>
  )
}
